import { createSelector } from 'reselect';
import { filterUsers } from './UserService';

export const selectUsersState = (state) => state.adminPanel.usersData;

export const selectAllUsers = createSelector(
  [selectUsersState],
  (usersState) => usersState.usersList,
);

export const selectSupervisorsList = createSelector(
  [selectUsersState],
  (usersState) => usersState.supervisorsList,
);

export const selectSupervisorsLoading = createSelector(
  [selectUsersState],
  (usersState) => usersState.supervisorsLoading,
);

export const selectSupervisorsPage = createSelector(
  [selectUsersState],
  (usersState) => usersState.supervisorsPage,
);

export const selectSupervisorsHasMore = createSelector(
  [selectUsersState],
  (usersState) => usersState.supervisorsHasMore,
);

export const selectTotalUsersCount = createSelector(
  [selectUsersState],
  (usersState) => usersState.totalCount,
);

export const selectUsersLoading = createSelector(
  [selectUsersState],
  (usersState) => usersState.loading,
);

export const selectListError = createSelector(
  [selectUsersState],
  (usersState) => usersState.listError,
);

export const selectSuccessMessage = createSelector(
  [selectUsersState],
  (usersState) => usersState.successMessage,
);

export const selectActionStatus = createSelector(
  [selectUsersState],
  (usersState) => usersState.actionStatus,
);

export const selectActionErrors = createSelector(
  [selectUsersState],
  (usersState) => usersState.actionErrors,
);


export const selectSearchTerm = createSelector(
  [selectUsersState],
  (usersState) => usersState.filters.searchTerm,
);

export const selectActiveFilter = createSelector(
  [selectUsersState],
  (usersState) => usersState.filters.activeFilter,
);

export const selectFilteredUsers = createSelector(
  [selectAllUsers, selectActiveFilter],
  (users, activeFilter) => filterUsers(users, activeFilter),
);

export const selectLoggedInUser = (state) => state.sessionContext?.UserView;

export const selectSessionContextOidc = (state) => state.sessionContextOidc;

// Session-context is populated by the header on app mount via
// fetchSessionContextOidcRequest → sessionContextOidcSaga (see
// containers/Header/saga/sessionContextOidcSaga.js). It applies the same
// normalization (UserService.normalizeCurrentUser) as the deprecated
// fetchCurrentUserSaga in this feature, so the shape is identical.
// All consumers now read from that single source of truth.
export const selectCurrentUser = (state) =>
  state.sessionContextOidc?.userView || null;

export const selectCurrentUserLoading = (state) =>
  !!state.sessionContextOidc?.loading;

export const selectCurrentUserError = createSelector(
  [selectUsersState],
  (usersState) => usersState.actionErrors.currentUser,
);


export const selectUsernameCheck = createSelector(
  [selectUsersState],
  (usersState) => usersState.usernameCheck,
);

export const selectUsernameChecking = createSelector(
  [selectUsersState],
  (usersState) => usersState.actionStatus.checkingUsername,
);

export const selectUsernameAvailability = createSelector(
  [selectUsernameCheck],
  (usernameCheck) => usernameCheck.available,
);

export const selectUsernameMessage = createSelector(
  [selectUsernameCheck],
  (usernameCheck) => usernameCheck.message,
);

export const selectUsernameError = createSelector(
  [selectUsernameCheck],
  (usernameCheck) => usernameCheck.error,
);


// Templates are also delivered as part of the session-context payload
// (response.templates → sessionContextOidc.userTemplates), so they share
// the same source of truth as currentUser above.
export const selectTemplates = (state) =>
  state.sessionContextOidc?.userTemplates || [];

export const selectTemplatesLoading = (state) =>
  !!state.sessionContextOidc?.loading;


export const selectIsAnyActionInProgress = createSelector(
  [selectActionStatus],
  (actionStatus) => Object.values(actionStatus).some(Boolean),
);

export const selectHasAnyError = createSelector(
  [selectListError, selectActionErrors],
  (listError, actionErrors) =>
    listError != null || Object.values(actionErrors).some((e) => e != null),
);

export const selectUserDetail = createSelector(
  [selectUsersState],
  (usersState) => usersState.userDetail,
);

export const selectUserDetailLoading = createSelector(
  [selectUsersState],
  (usersState) => usersState.actionStatus.fetchingUserDetail,
);

export const selectUserDetailError = createSelector(
  [selectUsersState],
  (usersState) => usersState.actionErrors.userDetail,
);


export const selectWorkloadState = createSelector(
  [selectUsersState],
  (usersState) => usersState.workload,
);

export const selectWorkloadWorkkards = createSelector(
  [selectWorkloadState],
  (workload) => workload.workkards,
);

export const selectWorkloadWorkkardsCount = createSelector(
  [selectWorkloadState],
  (workload) => workload.workkardsCount,
);

export const selectWorkloadActivities = createSelector(
  [selectWorkloadState],
  (workload) => workload.activities,
);

export const selectWorkloadActivitiesCount = createSelector(
  [selectWorkloadState],
  (workload) => workload.activitiesCount,
);

export const selectWorkloadSelectedUser = createSelector(
  [selectWorkloadState],
  (workload) => workload.selectedUser,
);

export const selectWorkloadActiveTab = createSelector(
  [selectWorkloadState],
  (workload) => workload.activeTab,
);

export const selectWorkloadLoading = createSelector(
  [selectWorkloadState],
  (workload) => workload.loading,
);

export const selectWorkloadTransferring = createSelector(
  [selectWorkloadState],
  (workload) => workload.transferring,
);

export const selectWorkloadError = createSelector(
  [selectWorkloadState],
  (workload) => workload.error,
);

export const selectWorkloadTransferError = createSelector(
  [selectWorkloadState],
  (workload) => workload.transferError,
);